/**
 * AdminAgentsManager - manage chat agents (used inside AdminSettingsPage tab)
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, X, Loader2, Shield, Headphones } from "lucide-react";

export default function AdminAgentsManager() {
    const [agents, setAgents] = useState([]);
    const [search, setSearch] = useState("");
    const [eligible, setEligible] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [working, setWorking] = useState(false);

    const loadAgents = async () => {
        const { data } = await api.get("/admin/chat/agents");
        setAgents(data);
    };
    const loadEligible = async (q = "") => {
        const { data } = await api.get(`/admin/chat/eligible-users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
        setEligible(data);
    };
    useEffect(() => { loadAgents(); }, []);

    const promote = async (userId) => {
        setWorking(true);
        try {
            await api.put(`/admin/chat/agents/${userId}`, { is_chat_agent: true });
            toast.success("Adicionado ao Chat");
            await loadAgents();
            await loadEligible(search);
        } catch (e) { toast.error(getApiErrorMessage(e)); }
        finally { setWorking(false); }
    };

    const remove = async (userId) => {
        if (!window.confirm("Remover este técnico do chat?")) return;
        setWorking(true);
        try {
            await api.delete(`/admin/chat/agents/${userId}`);
            toast.success("Técnico removido");
            await loadAgents();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
        finally { setWorking(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-agents-manager">
            <Card className="glass-card p-5 rounded-2xl">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div>
                        <h3 className="font-display font-semibold">Técnicos do Chat</h3>
                        <p className="text-sm text-muted-foreground">Utilizadores autorizados a atender clientes no chat de suporte.</p>
                    </div>
                    <Button
                        onClick={() => { setShowAdd(true); loadEligible(""); }}
                        className="btn-grad-purple text-white"
                        data-testid="admin-agents-add-button"
                    >
                        <Plus className="h-4 w-4 mr-1" /> Adicionar técnico
                    </Button>
                </div>

                {agents.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">Sem técnicos.</div>
                ) : (
                    <ul className="divide-y divide-white/5">
                        {agents.map((a) => (
                            <li key={a.id} className="py-3 flex items-center gap-3" data-testid={`admin-agent-${a.id}`}>
                                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#6B21D9] to-[#A78BFA] grid place-items-center text-white font-semibold relative">
                                    {(a.name || "?")[0]}
                                    {a.online && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-[#2DD4BF] border-2 border-card" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium flex items-center gap-2">
                                        {a.name}
                                        {a.role === "admin" ? (
                                            <Badge className="bg-[#6B21D9]/20 text-[#A78BFA] border-[#6B21D9]/30 border gap-1 text-[10px]">
                                                <Shield className="h-2.5 w-2.5" /> ADMIN
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-[#2DD4BF]/15 text-[#2DD4BF] border-[#2DD4BF]/25 border gap-1 text-[10px]">
                                                <Headphones className="h-2.5 w-2.5" /> TÉCNICO
                                            </Badge>
                                        )}
                                        {a.online && (
                                            <Badge className="bg-[#2DD4BF]/15 text-[#2DD4BF] border-[#2DD4BF]/25 border text-[10px]">online</Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{a.email}</div>
                                </div>
                                {a.role !== "admin" && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => remove(a.id)}
                                        disabled={working}
                                        data-testid={`admin-agent-remove-${a.id}`}
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {showAdd && (
                <Card className="glass-card p-5 rounded-2xl space-y-3">
                    <div className="flex items-center justify-between gap-3">
                        <h3 className="font-display font-semibold">Promover cliente a técnico</h3>
                        <Button variant="ghost" size="icon" onClick={() => setShowAdd(false)}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Pesquisar nome / email / empresa..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); loadEligible(e.target.value); }}
                            className="pl-9"
                            data-testid="admin-agents-search-input"
                        />
                    </div>
                    {eligible.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">Sem clientes correspondentes.</p>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {eligible.map((u) => (
                                <li key={u.id} className="py-2 flex items-center gap-3" data-testid={`admin-eligible-${u.id}`}>
                                    <div className="h-8 w-8 rounded-full bg-secondary grid place-items-center text-sm">
                                        {(u.name || "?")[0]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{u.name}</div>
                                        <div className="text-xs text-muted-foreground">{u.email} {u.company && `· ${u.company}`}</div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => promote(u.id)}
                                        disabled={working}
                                        className="btn-grad-purple text-white"
                                        data-testid={`admin-agent-promote-${u.id}`}
                                    >
                                        {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                                        <span className="ml-1">Adicionar</span>
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            )}
        </div>
    );
}
